<script setup>
import { onMounted, watch } from 'vue';

const props = defineProps({
  content: {
    type: String,
    default: '',
  },
  length: {
    type: Number,
    default: 20,
  },
  placement: {
    type: String,
    default: 'auto',
  },
  type: {
    type: String,
    default: 'truncate',
    validator(value) {
      return ['truncate', 'truncateMiddle'].includes(value);
    },
  },
  maxWidth: {
    type: String,
    default: '',
  },
});

const text_ref = ref(null);
const is_overflowing = ref(false);

const should_show_tooltip = computed(() => {
  // For max-width-based truncation, check if element is overflowing
  if (props.maxWidth) {
    return is_overflowing.value;
  }
  // For length-based truncation, show tooltip when content is longer than length
  return props.content?.length > props.length;
});

function checkOverflow() {
  if (text_ref.value) {
    // Use setTimeout to ensure browser has finished rendering
    setTimeout(() => {
      if (text_ref.value) {
        const scrollWidth = text_ref.value.scrollWidth;
        const clientWidth = text_ref.value.clientWidth;
        is_overflowing.value = scrollWidth > clientWidth;
      }
    }, 10);
  }
}

// Watch for content/width changes after mount
watch([() => props.content, () => props.maxWidth], () => {
  checkOverflow();
});

watch(text_ref, () => {
  checkOverflow();
});

onMounted(() => {
  checkOverflow();
});
</script>

<template>
  <!-- Width-based truncation with maxWidth -->
  <span
    v-if="maxWidth && should_show_tooltip"
    ref="text_ref"
    v-tippy="{ content, placement }"
    class="inline-block overflow-hidden text-ellipsis whitespace-nowrap align-middle"
    :style="{ maxWidth }"
  >
    {{ content }}
  </span>
  <span
    v-else-if="maxWidth"
    ref="text_ref"
    class="inline-block overflow-hidden text-ellipsis whitespace-nowrap align-middle"
    :style="{ maxWidth }"
  >
    {{ content }}
  </span>

  <!-- Length-based truncation -->
  <span v-else-if="should_show_tooltip" v-tippy="{ content, placement }">
    {{ $filters[type](content, length) }}
  </span>
  <span v-else>
    {{ $filters[type](content, length) }}
  </span>
</template>
